import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaSchool,
  FaUserCheck,
  FaBookOpen,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "./NonFormalEducation.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const IconGradCap = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12L4 20l20 8 20-8-20-8z" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
    <path d="M13 24v9c0 2.5 5 5 11 5s11-2.5 11-5v-9" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
    <path d="M40 20v10" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.3 2 4.5 5.6 4c2.1-.3 4 .8 6.4 3 2.4-2.2 4.3-3.3 6.4-3 3.6.5 5.2 4.3 3.6 7.7C19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.8 20c.7-3.6 3.3-5.6 6.2-5.6s5.5 2 6.2 5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M15.5 5.4a3.2 3.2 0 010 6.2M18.8 20c-.5-2.6-1.9-4.4-3.8-5.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconHandHeart = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 38c-4-4-7-8-7-13a7 7 0 0114-1 7 7 0 0114 1c0 5-3 9-7 13l-7 7-7-7z"
      stroke="#ffffff"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 34c-3 3-6 7-6 12 0 5 4 9 9 9h9c4 0 7-2 10-5l9-9c1.5-1.5 1.5-3.7 0-5-1.4-1.3-3.4-1.2-4.8.1l-6.2 6"
      fill="#ef7f3a"
    />
  </svg>
);

/* ------------------------------------------------------------------------------------ */

const NonFormalEducation = () => {
  const initiatives = [
    {
      title: "Training Programs",
      text: "We prepare low-cost teaching aids and train facilitators on center management and record-keeping.",
      icon: FaSchool,
    },
    {
      title: "Joyful Learning",
      text: "Facilitators use locally made materials for a play-way method of learning, with technical input from Vikash.",
      icon: FaUserCheck,
    },
    {
      title: "Co-Curricular Activities",
      text: "Centers observe national days and organize music, quiz, sports, and game-based activities.",
      icon: FaBookOpen,
    },
    {
      title: "Advocacy & Awareness",
      text: "Facilitators and villagers lead awareness campaigns and literacy marches to create lasting impact.",
      icon: FaMapMarkerAlt,
    },
    {
      title: "Community Library",
      text: "We maintain a library of over 5,000 books at Vigyan Ashram, Kundra for facilitators and learners.",
      icon: FaBookOpen,
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="nfe-hero">
        <div
          className="nfe-hero__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Non-Formal.png)",
          }}
        ></div>
        <div className="nfe-hero__overlay"></div>
        <div className="nfe-hero__dots"></div>

        <div className="nfe-hero__logo">
          <IconHandHeart />
        </div>

        <svg className="nfe-hero__wave" viewBox="0 0 200 260" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 2c30 20-20 45 10 65s45-10 60 15-10 40 20 60"
            stroke="#ef7f3a"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>

        <Container className="nfe-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Non-Formal
            <br />
            <span>Education</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Vikash promotes non-formal education by providing flexible
            learning opportunities and resources for skill development.
          </motion.p>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="nfe-intro">
        <div className="nfe-intro__blob"></div>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="nfe-eyebrow">
                  <span className="nfe-eyebrow__dash"></span>
                  Education Beyond the Classroom
                </div>
                <h2 className="nfe-heading">
                  Learning That Empowers
                  <br />
                  Every Child, Everywhere.
                </h2>
                <span className="nfe-underline"></span>
                <p className="nfe-text">
                  Since 1988, Vikash has been a pioneer in non-formal
                  education, starting with 50 centers in Puri district and
                  expanding to Koraput. We focus on early childhood
                  education and creating accessible learning environments
                  in spaces arranged by the community, with a special
                  emphasis on joyful, practical learning methods.
                </p>
              </motion.div>

              <motion.div className="col-lg-6" variants={staggerItem}>
                <Row className="g-3">
                  <Col xs={6}>
                    <img
                      src="/assets/images/Subsections/ne1.jpg"
                      alt="Non-formal education center"
                      className="nfe-intro__img"
                    />
                  </Col>
                  <Col xs={6}>
                    <img
                      src="/assets/images/Subsections/ne2.jpg"
                      alt="Children learning through play"
                      className="nfe-intro__img nfe-intro__img--down"
                    />
                  </Col>
                </Row>

                <div className="nfe-highlight">
                  <div className="nfe-highlight__icon">
                    <IconGradCap />
                  </div>
                  <div>
                    <strong>Empowering minds. Building brighter futures.</strong>
                    <p>Education is the foundation of lasting change.</p>
                  </div>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Our Approach Section */}
      <section className="nfe-approach">
        <div className="nfe-approach__dots"></div>
        <div className="nfe-approach__leaf">
          <svg viewBox="0 0 120 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60 150V40" stroke="#cddccf" strokeWidth="3" />
            <path d="M60 60c-15-15-35-15-45-5 10 15 30 15 45 5z" fill="#cddccf" />
            <path d="M60 90c15-15 35-15 45-5-10 15-30 15-45 5z" fill="#cddccf" />
            <path d="M60 120c-12-12-28-12-36-4 8 12 24 12 36 4z" fill="#cddccf" />
          </svg>
        </div>

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center nfe-approach__head"
          >
            <div className="nfe-eyebrow nfe-eyebrow--center">
              <span className="nfe-eyebrow__dash"></span>
              Our Approach to Non-Formal Learning
              <span className="nfe-eyebrow__dash"></span>
            </div>
            <h2 className="nfe-heading">
              Comprehensive Support. Enriching Futures.
            </h2>
            <p className="nfe-subtext">
              We provide comprehensive support, from training facilitators
              to enriching the curriculum.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="nfe-card h-100">
                      <Card.Body>
                        <div className="nfe-card__icon">
                          <item.icon aria-hidden="true" />
                        </div>
                        <Card.Title as="h5">{item.title}</Card.Title>
                        <span className="nfe-card__underline"></span>
                        <Card.Text>{item.text}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="nfe-cta">
        <div className="nfe-cta__dots"></div>
        <div className="nfe-cta__circle"></div>
        <div className="nfe-cta__leaf">
          <svg viewBox="0 0 120 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60 150V40" stroke="#f4d9c6" strokeWidth="3" />
            <path d="M60 60c-15-15-35-15-45-5 10 15 30 15 45 5z" fill="#f4d9c6" />
            <path d="M60 90c15-15 35-15 45-5-10 15-30 15-45 5z" fill="#f4d9c6" />
            <path d="M60 120c-12-12-28-12-36-4 8 12 24 12 36 4z" fill="#f4d9c6" />
          </svg>
        </div>

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <Row className="align-items-center g-4">
              <Col lg={3} className="text-center d-none d-lg-block">
                <div className="nfe-cta__illustration">
                  <svg viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="20" y="90" width="70" height="14" rx="2" fill="#e07a3f" />
                    <rect x="24" y="76" width="62" height="14" rx="2" fill="#2f6b47" />
                    <rect x="20" y="62" width="70" height="14" rx="2" fill="#1f4d33" />
                    <path d="M30 62l25-10 25 10-25 10-25-10z" fill="#111111" />
                    <path d="M30 62v8c6 5 20 8 25 8s19-3 25-8v-8" stroke="#111111" strokeWidth="2" />
                    <line x1="70" y1="62" x2="70" y2="78" stroke="#111111" strokeWidth="1.5" />
                    <circle cx="70" cy="80" r="2" fill="#111111" />
                    <rect x="105" y="70" width="24" height="34" rx="3" fill="#4a7d5a" />
                    <circle cx="110" cy="66" r="6" fill="#e8b64a" />
                    <circle cx="118" cy="66" r="6" fill="#d97757" />
                    <circle cx="126" cy="66" r="6" fill="#3b6b8f" />
                  </svg>
                </div>
              </Col>

              <Col lg={6} className="text-center">
                <div className="nfe-cta__icon">
                  <IconHeart />
                </div>
                <h2 className="nfe-cta__title">Support Lifelong Learning</h2>
                <p className="nfe-cta__text">
                  Your contribution helps us provide valuable educational
                  materials and training for our non-formal learning
                  centers.
                </p>
                <div className="nfe-cta__actions">
                  <Button className="nfe-btn nfe-btn--solid" onClick={openDonation}>
                    <IconHeart /> Donate Now
                  </Button>
                  <Button
                    className="nfe-btn nfe-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUsers /> Join Us
                  </Button>
                </div>
              </Col>

              <Col lg={3}></Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default NonFormalEducation;